import React, { useEffect, useState, useRef, Component } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchRoute, RouteData, RouteError } from '../../lib/routing';
import { Navigation, MapPin, Loader2, AlertTriangle, CornerUpRight, CornerUpLeft, ArrowUp, Map as MapIcon, ChevronDown, ChevronUp, Clock, Crosshair, Volume2, VolumeX } from 'lucide-react';
// Component to force map resize on mount
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 250);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}
interface NavigationMapProps {
  destinationLat: number;
  destinationLng: number;
  destinationName: string;
  destinationAddress?: string;
  compact?: boolean;
  onRouteUpdate?: (data: {
    distance: number;
    duration: number;
    currentInstruction: string;
    currentStepDistance: number;
    currentStepType?: string;
    currentStepModifier?: string;
  } | null) => void;
  onDriverPositionChange?: (lat: number, lng: number) => void;
  onRouteError?: (error: string) => void;
}
// Custom markers with animations
const createDriverIcon = () => L.divIcon({
  className: 'custom-driver-marker',
  html: `<div style="width: 20px; height: 20px; background-color: #3b82f6; border: 3px solid white; border-radius: 50%; box-shadow: 0 0 10px rgba(59, 130, 246, 0.8); transition: all 0.3s ease-out;"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});
const createDestinationIcon = () => L.divIcon({
  className: 'custom-dest-marker',
  html: `<div style="width: 24px; height: 24px; background-color: #00a870; border: 3px solid white; border-radius: 50%; box-shadow: 0 0 10px rgba(0, 168, 112, 0.8); display: flex; align-items: center; justify-content: center; animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;"><div style="width: 8px; height: 8px; background-color: white; border-radius: 50%;"></div></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12]
});
// Haversine distance in meters
function getDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
// Find closest point on polyline to driver position
function findClosestPointIndex(driverPos: [number, number], coords: [number, number][]): number {
  let minDist = Infinity;
  let closestIdx = 0;
  for (let i = 0; i < coords.length; i++) {
    const dist = getDistance(driverPos[0], driverPos[1], coords[i][0], coords[i][1]);
    if (dist < minDist) {
      minDist = dist;
      closestIdx = i;
    }
  }
  return closestIdx;
}
// Component to handle map auto-follow and bounds
function MapController({
  routeCoords,
  driverPos,
  destPos,
  followDriver,
  onUserPan
}: {
  routeCoords: [number, number][];
  driverPos: [number, number] | null;
  destPos: [number, number];
  followDriver: boolean;
  onUserPan: () => void;
}) {
  const map = useMap();
  const initialFitDoneRef = useRef(false);
  // Initial fit bounds
  useEffect(() => {
    if (!initialFitDoneRef.current && routeCoords.length > 0) {
      const bounds = L.latLngBounds(routeCoords);
      map.fitBounds(bounds, {
        padding: [80, 80],
        maxZoom: 18
      });
      initialFitDoneRef.current = true;
    } else if (!initialFitDoneRef.current && driverPos) {
      const bounds = L.latLngBounds([driverPos, destPos]);
      map.fitBounds(bounds, {
        padding: [80, 80],
        maxZoom: 16
      });
      initialFitDoneRef.current = true;
    }
  }, [map, routeCoords, driverPos, destPos]);
  // Auto-follow driver after initial fit — use higher zoom for navigation mode
  useEffect(() => {
    if (followDriver && driverPos && initialFitDoneRef.current) {
      map.setView(driverPos, 18, {
        animate: true,
        duration: 0.5
      });
    }
  }, [map, driverPos, followDriver]);
  // Detect user pan
  useEffect(() => {
    const handleMoveStart = () => {
      if (followDriver) {
        onUserPan();
      }
    };
    map.on('dragstart', handleMoveStart);
    return () => {
      map.off('dragstart', handleMoveStart);
    };
  }, [map, followDriver, onUserPan]);
  return null;
}
function getInstructionIcon(type?: string, modifier?: string) {
  if (modifier?.includes('left')) return <CornerUpLeft className="w-8 h-8 text-white" data-id="element-880" />;
  if (modifier?.includes('right')) return <CornerUpRight className="w-8 h-8 text-white" data-id="element-881" />;
  if (type === 'arrive') return <MapPin className="w-8 h-8 text-[#00a870]" data-id="element-882" />;
  return <ArrowUp className="w-8 h-8 text-white" data-id="element-883" />;
}
function formatDistance(meters: number) {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}
function formatDuration(seconds: number) {
  const mins = Math.ceil(seconds / 60);
  if (mins === 0) return 'Llegando...';
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hrs} h ${remainingMins} min`;
}
function translateInstruction(text: string): string {
  let t = text;
  // Full phrase replacements first
  t = t.replace(/\bTurn right\b/gi, 'Gira a la derecha');
  t = t.replace(/\bTurn left\b/gi, 'Gira a la izquierda');
  t = t.replace(/\bContinue straight\b/gi, 'Continúa recto');
  t = t.replace(/\bKeep right\b/gi, 'Mantente a la derecha');
  t = t.replace(/\bKeep left\b/gi, 'Mantente a la izquierda');
  t = t.replace(/\bMake a U-turn\b/gi, 'Haz un giro en U');
  t = t.replace(/\bYou have arrived\b/gi, 'Has llegado a tu destino');
  t = t.replace(/\bArrive at\b/gi, 'Llegaste a');
  t = t.replace(/\bHead\b/gi, 'Dirígete');
  t = t.replace(/\bslight right\b/gi, 'levemente a la derecha');
  t = t.replace(/\bslight left\b/gi, 'levemente a la izquierda');
  t = t.replace(/\bsharp right\b/gi, 'cerrada a la derecha');
  t = t.replace(/\bsharp left\b/gi, 'cerrada a la izquierda');
  // Direction words
  t = t.replace(/\bnorth\b/gi, 'al norte');
  t = t.replace(/\bsouth\b/gi, 'al sur');
  t = t.replace(/\beast\b/gi, 'al este');
  t = t.replace(/\bwest\b/gi, 'al oeste');
  t = t.replace(/\bnortheast\b/gi, 'al noreste');
  t = t.replace(/\bnorthwest\b/gi, 'al noroeste');
  t = t.replace(/\bsoutheast\b/gi, 'al sureste');
  t = t.replace(/\bsouthwest\b/gi, 'al suroeste');
  // Contextual word replacements
  t = t.replace(/\bonto\b/gi, 'por');
  t = t.replace(/\btoward\b/gi, 'hacia');
  t = t.replace(/\bon\b/gi, 'por');
  // Remaining standalone direction words (after phrase replacements)
  t = t.replace(/\bright\b/gi, 'la derecha');
  t = t.replace(/\bleft\b/gi, 'la izquierda');
  t = t.replace(/\bstraight\b/gi, 'recto');
  t = t.replace(/\bContinue\b/gi, 'Continúa');
  t = t.replace(/\bDestination\b/gi, 'Destino');
  t = t.replace(/\bRoundabout\b/gi, 'Rotonda');
  t = t.replace(/\bExit\b/gi, 'Salida');
  t = t.replace(/\bthe roundabout\b/gi, 'la rotonda');
  // Clean up double spaces
  t = t.replace(/\s+/g, ' ').trim();
  return t;
}
export function NavigationMap({
  destinationLat,
  destinationLng,
  destinationName,
  destinationAddress,
  compact = false,
  onRouteUpdate,
  onDriverPositionChange,
  onRouteError
}: NavigationMapProps) {
  const [driverPos, setDriverPos] = useState<[number, number] | null>(null);
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [followDriver, setFollowDriver] = useState(true);
  const [showRecenterButton, setShowRecenterButton] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [spanishVoice, setSpanishVoice] = useState<SpeechSynthesisVoice | null>(null);
  const destPos: [number, number] = [destinationLat, destinationLng];
  const watchIdRef = useRef<number | null>(null);
  const lastRouteFetchRef = useRef<number>(0);
  const lastSpokenIndexRef = useRef<number>(-1);
  // Load best Spanish voice (handles Chrome/Safari async voice loading)
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    function findSpanishVoice() {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length === 0) return;
      // Prefer: es-CR (Costa Rica) > es-MX > es-419 (Latin America) > any es-*
      const preferred = ['es-CR', 'es-MX', 'es-419', 'es-US', 'es-ES'];
      for (const lang of preferred) {
        const v = voices.find(voice => voice.lang === lang);
        if (v) {
          setSpanishVoice(v);
          return;
        }
      }
      const anySpanish = voices.find(voice => voice.lang.startsWith('es'));
      if (anySpanish) setSpanishVoice(anySpanish);
    }
    findSpanishVoice();
    window.speechSynthesis.addEventListener('voiceschanged', findSpanishVoice);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', findSpanishVoice);
    };
  }, []);
  // Voice navigation — speak instructions when step changes
  useEffect(() => {
    if (!voiceEnabled || !routeData || !routeData.steps[currentStepIndex]) return;
    if (lastSpokenIndexRef.current === currentStepIndex) return;
    const step = routeData.steps[currentStepIndex];
    const translated = translateInstruction(step.instruction);
    const distText = step.distance > 0 ? `En ${formatDistance(step.distance)}, ` : '';
    const text = step.type === 'arrive' ? 'Has llegado a tu destino' : `${distText}${translated}`;
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      // Small delay after cancel to ensure it completes (Safari fix)
      setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'es-ES';
        if (spanishVoice) utterance.voice = spanishVoice;
        utterance.rate = 1.05;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        window.speechSynthesis.speak(utterance);
      }, 50);
    }
    lastSpokenIndexRef.current = currentStepIndex;
  }, [currentStepIndex, routeData, voiceEnabled, spanishVoice]);
  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocalización no soportada por el navegador');
      setLoading(false);
      return;
    }
    const handleSuccess = async (position: GeolocationPosition) => {
      const newPos: [number, number] = [position.coords.latitude, position.coords.longitude];
      setDriverPos(newPos);
      setLoading(false);
      setError(null);
      // Notify parent of driver position change
      if (onDriverPositionChange) {
        onDriverPositionChange(newPos[0], newPos[1]);
      }
      // Check if driver is off-route (more than 50m from nearest point on polyline)
      let isOffRoute = false;
      if (routeData && routeData.coordinates.length > 0) {
        const closestIdx = findClosestPointIndex(newPos, routeData.coordinates);
        const distToRoute = getDistance(newPos[0], newPos[1], routeData.coordinates[closestIdx][0], routeData.coordinates[closestIdx][1]);
        if (distToRoute > 50) {
          isOffRoute = true;
          console.log('[NAV-MAP] Driver off-route, recalculating immediately');
        }
      }
      // Fetch route if: no route, 15+ seconds since last fetch, or off-route
      const now = Date.now();
      if (!routeData || now - lastRouteFetchRef.current > 15000 || isOffRoute) {
        setRecalculating(true);
        lastRouteFetchRef.current = now;
        const result = await fetchRoute(newPos[0], newPos[1], destPos[0], destPos[1]);
        setRecalculating(false);
        if (result && 'error' in result) {
          // Route error
          console.error('Route error:', result.error);
          if (onRouteError) {
            onRouteError(result.error);
          }
          if (onRouteUpdate) {
            onRouteUpdate(null);
          }
        } else if (result) {
          // Success
          setRouteData(result);
          setCurrentStepIndex(0); // Reset step index on new route
          if (onRouteUpdate && result.steps.length > 0) {
            onRouteUpdate({
              distance: result.distance,
              duration: result.duration,
              currentInstruction: result.steps[0].instruction,
              currentStepDistance: result.steps[0].distance,
              currentStepType: result.steps[0].type,
              currentStepModifier: result.steps[0].modifier
            });
          }
        }
      } else if (routeData && routeData.steps.length > 0) {
        // Check if driver is close to current step end — advance step if within 30m
        const currentStep = routeData.steps[currentStepIndex];
        if (currentStep && currentStepIndex < routeData.steps.length - 1) {
          // Approximate step end position by summing distances
          // (OSRM doesn't give exact coords per step, so we estimate)
          // For simplicity, check if driver is within 30m of the next step start
          // A better approach would use step geometry, but OSRM steps don't include it
          // So we'll use a heuristic: if currentStepDistance is very small, advance
          const distToStepEnd = currentStep.distance;
          if (distToStepEnd < 30) {
            console.log('[NAV-MAP] Advancing to next step');
            const nextIdx = currentStepIndex + 1;
            setCurrentStepIndex(nextIdx);
            if (onRouteUpdate && routeData.steps[nextIdx]) {
              onRouteUpdate({
                distance: routeData.distance,
                duration: routeData.duration,
                currentInstruction: routeData.steps[nextIdx].instruction,
                currentStepDistance: routeData.steps[nextIdx].distance,
                currentStepType: routeData.steps[nextIdx].type,
                currentStepModifier: routeData.steps[nextIdx].modifier
              });
            }
          }
        }
      }
    };
    const handleError = (err: GeolocationPositionError) => {
      console.error('Geolocation error:', err);
      setError('No se pudo obtener tu ubicación. Verifica los permisos.');
      setLoading(false);
    };
    // Get initial position
    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
      enableHighAccuracy: true
    });
    // Watch position
    watchIdRef.current = navigator.geolocation.watchPosition(handleSuccess, handleError, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 10000
    });
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [destinationLat, destinationLng, routeData, currentStepIndex]);
  const handleRecenter = () => {
    setFollowDriver(true);
    setShowRecenterButton(false);
  };
  const handleUserPan = () => {
    setFollowDriver(false);
    setShowRecenterButton(true);
  };
  if (loading) {
    return <div className="w-full h-full min-h-[400px] bg-[#1A1A1A] rounded-2xl border border-white/10 flex flex-col items-center justify-center" data-id="element-884">
        <Loader2 className="w-10 h-10 text-[#00a870] animate-spin mb-4" data-id="element-885" />
        <p className="text-white/50 font-bold" data-id="element-886">Obteniendo tu ubicación...</p>
      </div>;
  }
  if (error && !driverPos) {
    return <div className="w-full h-full min-h-[400px] bg-[#1A1A1A] rounded-2xl border border-red-500/20 flex flex-col items-center justify-center p-6 text-center" data-id="element-887">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" data-id="element-888" />
        <p className="text-white font-bold mb-2" data-id="element-889">Error de GPS</p>
        <p className="text-white/50 text-sm" data-id="element-890">{error}</p>
      </div>;
  }
  // Trim polyline to only show remaining route (from closest point forward)
  let trimmedCoords = routeData?.coordinates || [];
  if (driverPos && routeData && routeData.coordinates.length > 0) {
    const closestIdx = findClosestPointIndex(driverPos, routeData.coordinates);
    trimmedCoords = routeData.coordinates.slice(closestIdx);
  }
  const currentStep = routeData?.steps?.[currentStepIndex];
  const nextSteps = routeData?.steps?.slice(currentStepIndex + 1).filter(s => s.type !== 'arrive') || [];
  return <div className="w-full h-full flex flex-col relative rounded-2xl overflow-hidden border border-white/10 bg-[#1A1A1A]" data-id="element-891">
      {/* Map Area */}
      <div className="flex-1 relative min-h-[200px]" data-id="element-892">
        <MapContainer center={driverPos || destPos} zoom={15} className="absolute inset-0 w-full h-full z-0" zoomControl={false} data-id="element-893">
          <MapResizer data-id="element-894" />
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>' data-id="element-895" />

          <MapController routeCoords={trimmedCoords} driverPos={driverPos} destPos={destPos} followDriver={followDriver} onUserPan={handleUserPan} data-id="element-896" />

          {/* Route polyline shadow (darker, thicker) */}
          {trimmedCoords.length > 0 && <Polyline positions={trimmedCoords} color="#000000" weight={9} opacity={0.3} lineCap="round" lineJoin="round" data-id="element-897" />}

          {/* Route polyline main */}
          {trimmedCoords.length > 0 && <Polyline positions={trimmedCoords} color="#00a870" weight={7} opacity={0.9} lineCap="round" lineJoin="round" data-id="element-898" />}

          {/* Driver position with pulsing accuracy circle */}
          {driverPos && <>
              <Circle center={driverPos} radius={30} pathOptions={{
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.1,
            weight: 1,
            opacity: 0.3
          }} className="animate-pulse" data-id="element-899" />
              <Marker position={driverPos} icon={createDriverIcon()} data-id="element-900" />
            </>}

          <Marker position={destPos} icon={createDestinationIcon()} data-id="element-901" />
        </MapContainer>

        {/* Map Controls (Right side) */}
        <div className="absolute bottom-6 right-4 z-[400] flex flex-col gap-3" data-id="element-902">
          <button onClick={() => {
          setVoiceEnabled(!voiceEnabled);
          if (voiceEnabled && 'speechSynthesis' in window) {
            window.speechSynthesis.cancel();
          } else if (!voiceEnabled && 'speechSynthesis' in window && routeData?.steps[currentStepIndex]) {
            const utterance = new SpeechSynthesisUtterance(translateInstruction(routeData.steps[currentStepIndex].instruction));
            utterance.lang = 'es-ES';
            window.speechSynthesis.speak(utterance);
          }
        }} className={`w-12 h-12 rounded-full shadow-xl flex items-center justify-center active:scale-95 transition-transform ${voiceEnabled ? 'bg-white text-[#0B0B0B]' : 'bg-[#1A1A1A] text-white/50 border border-white/10'}`} data-id="element-903">
            {voiceEnabled ? <Volume2 className="w-6 h-6" data-id="element-904" /> : <VolumeX className="w-6 h-6" data-id="element-905" />}
          </button>

          {showRecenterButton && <button onClick={handleRecenter} className="w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center active:scale-95 transition-transform" data-id="element-906">
              <Crosshair className="w-6 h-6 text-[#0B0B0B]" data-id="element-907" />
            </button>}
        </div>

        {/* Recalculating indicator */}
        {recalculating && <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[400] bg-[#0B0B0B]/90 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 flex items-center gap-2 shadow-lg" data-id="element-908">
            <Loader2 className="w-3.5 h-3.5 text-[#00a870] animate-spin" data-id="element-909" />
            <span className="text-white/70 text-xs font-bold" data-id="element-910">
              Recalculando...
            </span>
          </div>}

        {/* Top Overlay: ETA, Distance & Customer Info (Hidden in compact mode) */}
        {!compact && <div className="absolute top-4 left-4 right-4 z-[400] flex justify-between items-start pointer-events-none" data-id="element-911">
            <div className="bg-[#0B0B0B]/90 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-2xl pointer-events-auto" data-id="element-912">
              {routeData ? <>
                  <div className="flex items-end gap-2 mb-1" data-id="element-913">
                    <span className="text-3xl font-black text-[#00a870] leading-none" data-id="element-914">
                      {formatDuration(routeData.duration)}
                    </span>
                  </div>
                  <span className="text-white/60 font-bold text-sm" data-id="element-915">
                    {formatDistance(routeData.distance)} restantes
                  </span>
                </> : <span className="text-white/40 font-bold text-sm" data-id="element-916">
                  Calculando ruta...
                </span>}
              <div className="mt-3 pt-3 border-t border-white/10" data-id="element-917">
                <p className="text-white font-black text-sm leading-tight" data-id="element-918">
                  {destinationName}
                </p>
                {destinationAddress && <p className="text-white/40 text-xs mt-0.5 leading-snug" data-id="element-919">
                    {destinationAddress}
                  </p>}
              </div>
            </div>
          </div>}
      </div>

      {/* Bottom Instructions Panel (Hidden in compact mode) */}
      {!compact && <div className="bg-[#141414] border-t border-white/10 shrink-0 z-[400]" data-id="element-920">
          {currentStep ? <>
              {/* Current instruction — large and prominent */}
              <div className="p-5 pb-3" data-id="element-921">
                <div className="flex items-center gap-4" data-id="element-922">
                  <div className="w-16 h-16 rounded-2xl bg-[#00a870]/15 flex items-center justify-center shrink-0" data-id="element-923">
                    {getInstructionIcon(currentStep.type, currentStep.modifier)}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-924">
                    <p className="text-2xl font-black text-white leading-tight mb-1 truncate" data-id="element-925">
                      {translateInstruction(currentStep.instruction)}
                    </p>
                    <div className="flex items-center gap-3" data-id="element-926">
                      <span className="text-[#00a870] font-black text-lg" data-id="element-927">
                        {formatDistance(currentStep.distance)}
                      </span>
                      {routeData && <span className="flex items-center gap-1 text-white/40 text-sm font-bold" data-id="element-928">
                          <Clock className="w-3.5 h-3.5" data-id="element-929" />
                          {formatDuration(routeData.duration)} total
                        </span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Upcoming steps toggle */}
              {nextSteps.length > 0 && <div className="px-5 pb-4" data-id="element-930">
                  <button onClick={() => setShowSteps(!showSteps)} className="w-full flex items-center justify-between py-2.5 px-4 rounded-xl bg-white/[0.03] border border-white/5 active:scale-[0.98] transition-transform" data-id="element-931">
                    <span className="text-white/40 text-xs font-bold uppercase tracking-wider" data-id="element-932">
                      {nextSteps.length}{' '}
                      {nextSteps.length === 1 ? 'paso' : 'pasos'} más
                    </span>
                    {showSteps ? <ChevronUp className="w-4 h-4 text-white/30" data-id="element-933" /> : <ChevronDown className="w-4 h-4 text-white/30" data-id="element-934" />}
                  </button>

                  {showSteps && <div className="mt-3 space-y-1 max-h-40 overflow-y-auto" data-id="element-935">
                      {nextSteps.slice(0, 5).map((step, idx) => <div key={idx} className="flex items-center gap-3 py-2 px-1" data-id="element-936">
                          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-id="element-937">
                            {getInstructionIcon(step.type, step.modifier)}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-938">
                            <p className="text-white/70 text-sm font-bold truncate" data-id="element-939">
                              {translateInstruction(step.instruction)}
                            </p>
                          </div>
                          <span className="text-white/30 text-xs font-bold shrink-0" data-id="element-940">
                            {formatDistance(step.distance)}
                          </span>
                        </div>)}
                      {nextSteps.length > 5 && <p className="text-white/20 text-xs text-center pt-1" data-id="element-941">
                          +{nextSteps.length - 5} más
                        </p>}
                    </div>}
                </div>}
            </> : <div className="p-5 flex items-center gap-4" data-id="element-942">
              <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center shrink-0" data-id="element-943">
                <MapIcon className="w-8 h-8 text-white/30" data-id="element-944" />
              </div>
              <div data-id="element-945">
                <p className="text-xl font-bold text-white/50" data-id="element-946">
                  Calculando ruta...
                </p>
                <p className="text-white/25 text-sm mt-1" data-id="element-947">
                  Obteniendo indicaciones
                </p>
              </div>
            </div>}
        </div>}
    </div>;
}